import { spanToPercent } from "@/ds";
import {
  buildTimeline,
  formatDate,
  monthEnd,
  positionInterval,
  todayIso,
} from "@/modules/carga-trabalho";
import type { CargaTrabalhoIntervalo, Timeline } from "@/modules/carga-trabalho";
import {
  CATEGORIA_LABEL,
  ROTULO_FECHAMENTO,
  ROTULO_PLANEJAMENTO,
  ROTULO_REMOVIDO,
  SEM_RECURSO,
} from "@/modules/recursos/simulacao";
import type { LinhaComparada, ObraComparada, VersaoComparada } from "@/modules/recursos/simulacao";

import { ICONE_CATEGORIA } from "./comparar-simulacao-dialog.constants";
import type {
  BarraComparada,
  IndicadoresComparar,
  LinhaGantt,
  RecursoComparado,
  TrechoComparado,
  VersaoNome,
} from "./types-comparar-simulacao-dialog";

/** Primeiro dia do mês `delta` meses antes/depois do mês de `data`. */
function inicioDoMes(data: string, delta: number): string {
  const [ano = 1970, mes = 1] = data.split("-").map(Number);
  return new Date(Date.UTC(ano, mes - 1 + delta, 1)).toISOString().slice(0, 10);
}

/**
 * A escala cobre só o período das alterações, com um mês de margem de cada lado: o Gantt
 * já abre no trecho alterado, sem rolar desde janeiro. Etapas fora dele aparecem cortadas.
 */
export function timelineDe(obras: ObraComparada[]): Timeline {
  const datas = obras
    .flatMap((obra) =>
      obra.linhas.flatMap((linha) =>
        [linha.atual.intervalo, linha.simulado.intervalo].flatMap((i) =>
          i ? [i.inicio, i.fim] : [],
        ),
      ),
    )
    .sort();
  const hoje = todayIso();
  const primeira = datas[0] ?? hoje;
  const ultima = datas[datas.length - 1] ?? hoje;
  return buildTimeline({
    inicio: inicioDoMes(primeira, -1),
    fim: monthEnd(inicioDoMes(ultima, 1).slice(0, 7)),
  });
}

/** "05/02 – 20/04": as datas dentro da barra, como no Figma 9553:41389. */
function periodoNaBarra(intervalo: CargaTrabalhoIntervalo): string {
  return `${formatDate(intervalo.inicio).slice(0, 5)} – ${formatDate(intervalo.fim).slice(0, 5)}`;
}

function barra(
  chave: string,
  intervalo: CargaTrabalhoIntervalo,
  timeline: Timeline,
  rotulo: string,
  opcoes: Pick<BarraComparada, "tone" | "pontaInicio" | "pontaFim"> & { texto?: string },
): BarraComparada[] {
  const posicao = positionInterval(intervalo, timeline);
  if (!posicao) return [];
  const { texto = rotulo, ...resto } = opcoes;
  return [
    {
      chave,
      start: posicao.left,
      end: posicao.left + posicao.width,
      titulo: `${rotulo}: ${formatDate(intervalo.inicio)} a ${formatDate(intervalo.fim)}`,
      rotulo: texto || undefined,
      ...resto,
    },
  ];
}

/**
 * As barras de um recurso como as do Gantt principal: planejamento (azul), obra (escuro, com as
 * datas dentro) e fechamento (verde). Sem etapas (fornecedor e local), só a obra; cada indicador
 * desligado tira a sua barra.
 */
function barrasDaVersao(
  dados: VersaoComparada,
  intervalo: CargaTrabalhoIntervalo,
  timeline: Timeline,
  indicadores: IndicadoresComparar,
): BarraComparada[] {
  const planejamento = indicadores.planejamento ? (dados.etapas?.planejamento ?? null) : null;
  const fechamento = indicadores.fechamento ? (dados.etapas?.fechamento ?? null) : null;
  // A Obra vai primeiro: as pontas de 1 dia vêm depois e ficam desenhadas por cima dela.
  return [
    ...(indicadores.obra
      ? barra("obra", intervalo, timeline, dados.rotulo, {
          texto: periodoNaBarra(intervalo),
          tone: "primary",
          pontaInicio: !planejamento,
          pontaFim: !fechamento,
        })
      : []),
    ...(planejamento
      ? barra("planejamento", planejamento, timeline, ROTULO_PLANEJAMENTO, {
          texto: "",
          tone: "info",
          pontaInicio: true,
          pontaFim: false,
        })
      : []),
    ...(fechamento
      ? barra("fechamento", fechamento, timeline, ROTULO_FECHAMENTO, {
          texto: "",
          tone: "success",
          pontaInicio: false,
          pontaFim: true,
        })
      : []),
  ];
}

/** `left` e `width` (em %) da faixa, arredondados como os das barras do `GanttBar`, para os limites coincidirem. */
function percentStyle({ left, width }: { left: number; width: number }) {
  const span = spanToPercent({ start: left, end: left + width });
  return { left: `${span.left}%`, width: `${span.width}%` };
}

/**
 * Uma faixa por limite que mudou: do menor ao maior início e do menor ao maior fim, atual e
 * simulado.
 */
function trechosDoDeslocamento(linha: LinhaComparada, timeline: Timeline): TrechoComparado[] {
  const { atual, simulado, deslocamentoDias, deslocamentoFimDias } = linha;
  if (!atual.intervalo || !simulado.intervalo) return [];
  const limites = [
    deslocamentoDias !== null ? [atual.intervalo.inicio, simulado.intervalo.inicio] : null,
    deslocamentoFimDias !== null ? [atual.intervalo.fim, simulado.intervalo.fim] : null,
  ];
  return limites.flatMap((datas, indice) => {
    if (!datas) return [];
    const [inicio = "", fim = ""] = [...datas].sort();
    const posicao = positionInterval({ inicio, fim }, timeline);
    return posicao ? [{ chave: String(indice), ...percentStyle(posicao) }] : [];
  });
}

function diasDoDeslocamento(dias: number): string {
  const total = Math.abs(dias);
  return `${dias > 0 ? "+" : "-"}${total} ${total === 1 ? "dia" : "dias"}`;
}

function descreverDeslocamento(linha: LinhaComparada): string | null {
  const { atual, simulado, deslocamentoDias, deslocamentoFimDias } = linha;
  if (!atual.intervalo || !simulado.intervalo) return null;
  const partes = [
    deslocamentoDias !== null
      ? `Início ${formatDate(atual.intervalo.inicio)} × ${formatDate(simulado.intervalo.inicio)} (${diasDoDeslocamento(deslocamentoDias)})`
      : null,
    deslocamentoFimDias !== null
      ? `Fim ${formatDate(atual.intervalo.fim)} × ${formatDate(simulado.intervalo.fim)} (${diasDoDeslocamento(deslocamentoFimDias)})`
      : null,
  ].filter(Boolean);
  return partes.length > 0 ? partes.join(" · ") : null;
}

/** Figma 9581:41055: o recurso que sai fica riscado; o que entra, em negrito. */
function recursoDaLinha(linha: LinhaComparada): RecursoComparado {
  const saiu = linha.simulado.rotulo === ROTULO_REMOVIDO;
  const anterior = linha.atual.recurso;
  const placeholder = Object.values(SEM_RECURSO).includes(anterior);
  const mesmoNome = anterior === linha.simulado.recurso;
  const igual = mesmoNome && !saiu;

  return {
    icone: `pi ${ICONE_CATEGORIA[linha.categoria]}`,
    categoria: CATEGORIA_LABEL[linha.categoria],
    riscado: !igual && !placeholder && anterior ? anterior : null,
    destaque: igual ? anterior : saiu ? null : linha.simulado.recurso,
    contexto: linha.contexto || null,
  };
}

/** As linhas Atual e Simulado de cada recurso da obra. */
export function linhasDaObra(
  obra: ObraComparada,
  timeline: Timeline,
  indicadores: IndicadoresComparar,
): LinhaGantt[] {
  return obra.linhas.flatMap((linha) => {
    // O deslocamento é o da Obra: some junto com a barra dela.
    const deslocamento = indicadores.obra ? descreverDeslocamento(linha) : null;
    const trechos = indicadores.obra ? trechosDoDeslocamento(linha, timeline) : [];
    const versao = (nome: VersaoNome, dados: VersaoComparada): LinhaGantt => ({
      chave: `${obra.id}-${linha.chave}-${nome}`,
      grupo: obra.id,
      versao: nome,
      simulada: nome === "Simulado",
      acaoLabel: `${linha.recurso} (${nome})`,
      recurso: nome === "Atual" ? recursoDaLinha(linha) : null,
      barras: dados.intervalo ? barrasDaVersao(dados, dados.intervalo, timeline, indicadores) : [],
      trechos,
      deslocamento,
      semIntervalo: dados.intervalo ? null : dados.rotulo,
    });
    return [versao("Atual", linha.atual), versao("Simulado", linha.simulado)];
  });
}
